import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import ReactMarkdown from 'react-markdown'

import { ButtonRow } from './button-row'
import { CollapsableFiltersSummary } from './collapsable-filters'
import { ClickEditInput } from './input'
import { LoadingLabel } from './loader'
import { getCurrentAccountQRDetails } from '../api/apollo/graphql/company-client'
import {
  listSavedLostLinksReports,
  updateSavedLostLinkReport,
} from '../api/apollo/graphql/report-client'
import { DimensionOption, SelectedDimensions } from '../api/types'
import uplifter from '../assets/uplogo.png'
import { AvailableDimension } from '../context/reducers/graphql-reducer'
import { messages } from '../core/variables'
import useLogAction from '../hooks/useLogAction'
import { Logo } from '../pages/performance-report-page'
import styles from '../styles/lost-links-report-header.module.scss'
import { GetAccountDataSourceQuery } from '../__gql-types__/graphql'

interface LostLinksReportHeaderProps {
  dateRange?: string
  dataSourceData?: GetAccountDataSourceQuery
  isMobile: boolean
  onChangeFilters: (val: DimensionOption, item: any) => void
  availableDimensions: AvailableDimension[]
  selectedDimensions: SelectedDimensions
  currentReport: SavedReport | null
}

export default function LostLinksReportHeader({
  dateRange,
  dataSourceData,
  isMobile,
  onChangeFilters,
  availableDimensions,
  selectedDimensions,
  currentReport,
}: LostLinksReportHeaderProps) {
  const logAction = useLogAction()

  const { data: logoData } = useQuery(getCurrentAccountQRDetails)

  const [updateSavedReport] = useMutation(updateSavedLostLinkReport, {
    refetchQueries: [listSavedLostLinksReports],
  })

  const [titleValue, setTitleValue] = useState(
    currentReport
      ? currentReport.reportTitle
      : messages.defaultLostLinksReportTitle,
  )

  const useLogo = logoData?.currentAccount.logoLink || uplifter
  const logoLink =
    useLogo.indexOf('logos/PNG/uplogo.png') !== -1 ? uplifter : useLogo

  // UI display of data source details
  const dataSourceDescription = useMemo(() => {
    if (!dataSourceData || !dataSourceData.currentAccount.dataSource) return ''

    if (!dataSourceData.currentAccount.dataSource.connected) {
      return '**Metrics from:** *(Not connected)*'
    }

    const {
      ga4PropertyName,
      gaPropertyName,
      gaViewName,
    } = dataSourceData.currentAccount.dataSource

    switch (dataSourceData.currentAccount.dataSource.kind) {
      case 'GA_VIEW':
        return `**Metrics from:** Google Analytics (UA), property: **${gaPropertyName}**, view: **${gaViewName}**`
      case 'GA4_PROP':
        return `**Metrics from:** Google Analytics (GA4), property: **${ga4PropertyName}**`
      default:
        return '**Metrics from:** *(Not connected)*'
    }
  }, [dataSourceData])

  useEffect(() => {
    setTitleValue(
      currentReport?.reportTitle || messages.defaultLostLinksReportTitle,
    )
  }, [currentReport])

  return (
    <div className={styles.paperHeader}>
      {isMobile && (
        <div className={styles.logoWrapper}>
          <Logo logoLink={logoLink} loading={!logoData} />
        </div>
      )}
      <div className={styles.paperHeaderContent}>
        <ButtonRow className={styles.headerButtonRow}>
          <div className={styles.editTitleWrapper}>
            {currentReport === null ? (
              <h4>Other links audit</h4>
            ) : (
              <ClickEditInput
                id="report-title"
                name="report-title"
                className={styles.editTitle}
                value={titleValue}
                onChange={async (newValue: string) => {
                  if (newValue !== titleValue) {
                    setTitleValue(newValue)

                    await updateSavedReport({
                      variables: {
                        savedReportID: currentReport.savedReportID,
                        reportTitle: newValue,
                      },
                    })

                    logAction({
                      variables: {
                        action: 'saved-lost-link-reports-update-report-title',
                        extra: JSON.stringify({
                          ...currentReport,
                          reportTitle: newValue,
                        }),
                        websiteSection: 'report',
                        functionName: 'updateSavedReportTitle',
                        pagePath: window.location.pathname,
                      },
                    })
                  }
                }}
              />
            )}
          </div>
        </ButtonRow>
        <p>
          <strong>Date range:</strong>{' '}
          {dateRange || <LoadingLabel label="Loading" />}
        </p>
        {dataSourceDescription ? (
          <ReactMarkdown source={dataSourceDescription} />
        ) : (
          <p>
            <strong>Metrics from:</strong> <LoadingLabel label="Loading" />
          </p>
        )}
        <p>
          <strong>Other links:</strong>{' '}
          <span>
            Landing pages with UTMs which were not created in Uplifter.
          </span>
        </p>
        <CollapsableFiltersSummary
          data={availableDimensions || []}
          selectedDimensions={selectedDimensions}
          onChange={onChangeFilters}
        />
      </div>
      {!isMobile && (
        <div className={styles.logoWrapper}>
          <Logo logoLink={logoLink} loading={!logoData} />
        </div>
      )}
    </div>
  )
}
